import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  AppError,
  Button,
  Checkbox,
  DismissibleChip,
  EmptyState,
  Loader,
  Select,
  selectState as ss,
  type SelectMsg,
  type SelectState,
} from "@bfc-software/pallet";
import { match } from "ts-pattern";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import {
  useDeliverApi,
  useDeliverFetchClient,
} from "@bfc-software/bfc-web-ui/lib/deliver-api-client";
import { AppHeader } from "@bfc-software/bfc-web-ui/ui/shared/app-header";

import { DriversTable } from "./-components/drivers-table";
import { FilterBar } from "./-components/filter-bar";
import { RoutesTable, routeKey } from "./-components/routes-table";
import { RouteDrawer } from "./-components/route-drawer";
import { SummaryPanel } from "./-components/summary-panel";
import { WorklistTable } from "./-components/worklist-table";
import {
  PAGE_SIZE,
  WORKLIST_KEY,
  filterQuery,
  formatOpenRoute,
  parseOpenRoute,
  resolveFilters,
  sameFilters,
  validateDeliverSearch,
  type DeliverSearch,
} from "./-data/filters";
import { downloadBlob, isBlob } from "./-data/download";
import { jsonBody } from "./-data/json-body";
import styles from "./index.module.css";

const REFRESH_INTERVALS = ["30", "60", "120", "300"] as const;

type IntervalItem = ss.Item; // id: "30" | "60" | "120" | "300"

/**
 * Pure. A closed enum: the selected row is built from the value itself (label by
 * the same key as the list), so there is no lookup to miss.
 */
function intervalSelectState(a: {
  interval: string;
  t: (key: string) => string;
}): SelectState<IntervalItem> {
  const row = (id: string): IntervalItem => ({
    id,
    label: a.t(`list.interval.${id}`),
  });
  const [first, ...rest] = REFRESH_INTERVALS;
  return ss.always({
    label: ss.ariaLabel(a.t("list.interval.ariaLabel")),
    options: ss.fixed<IntervalItem>([row(first), ...rest.map(row)]),
    selected: row(a.interval),
  });
}

/**
 * Deliver's dashboard: the KPI strip and a filterable list of routes (or the
 * driver rollup over the same filters). Drill-downs -- stops, items, pieces,
 * returns, proof and the exception worklists -- hang off this screen next.
 *
 * Unlike Outbound this product has no warehouse in its URL: the service scopes
 * every request to the caller's organisation, and a warehouse is just another
 * optional filter on it.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/deliver/",
)({
  validateSearch: (search?: Record<string, unknown>): DeliverSearch =>
    validateDeliverSearch(search),
  component: RouteComponent,
});

export function RouteComponent() {
  const { lang, org } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.deliver,
  });

  const client = useDeliverApi();
  const fetchClient = useDeliverFetchClient();
  // Fixed for the life of the screen so the default window does not shift
  // under an open page; a reload or a Reset picks up a new day.
  const now = useMemo(() => new Date(), []);
  // The warehouses to filter by change rarely, so one read serves the visit.
  const tenants = client.useQuery(
    "get",
    "/app/deliver/api/tenants",
    undefined,
    { staleTime: 5 * 60 * 1000 },
  );

  // There is no "all warehouses": with none named in the URL the dashboard is
  // the first warehouse the caller may see, which is only known once that list
  // has arrived. Nothing is asked of the service until there is a warehouse to
  // ask about, so a first paint never shows -- or bills for -- everyone's data.
  const defaultTenantId = tenants.data?.rows[0]?.tenantId;
  const filters = resolveFilters(search, now, defaultTenantId);
  const warehouseReady = filters.tenantId !== undefined;
  const view = search.view ?? "routes";
  const worklist = search.worklist;
  const page = search.page ?? 0;

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [interval, setIntervalSeconds] = useState<string>("60");
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [exportFailed, setExportFailed] = useState(false);
  const onIntervalMsg = (msg: SelectMsg<IntervalItem>) =>
    match(msg)
      .with({ kind: "picked" }, ({ item }) =>
        setIntervalSeconds(String(item.id)),
      )
      .otherwise(() => {}); // static `always`
  const [worklistUpdatedAt, setWorklistUpdatedAt] = useState(0);
  // A worklist reports its own fetch time once it mounts. Until then the last
  // worklist's time must not stand in for it, so it is cleared the moment the
  // worklist changes (setting state while rendering, for a value derived from a
  // prop, is React's documented way to do this without a stale frame).
  const [reportedFor, setReportedFor] = useState(search.worklist);
  if (reportedFor !== search.worklist) {
    setReportedFor(search.worklist);
    setWorklistUpdatedAt(0);
  }

  const refetchInterval = autoRefresh ? Number(interval) * 1000 : false;
  const query = filterQuery(filters);

  const summary = client.useQuery(
    "get",
    "/app/deliver/api/summary",
    { params: { query } },
    { enabled: warehouseReady, refetchInterval },
  );

  const routes = client.useQuery(
    "get",
    "/app/deliver/api/routes",
    { params: { query: { ...query, page, size: PAGE_SIZE } } },
    {
      enabled: warehouseReady && view === "routes" && !worklist,
      refetchInterval,
    },
  );

  const drivers = client.useQuery(
    "get",
    "/app/deliver/api/drivers",
    { params: { query } },
    {
      enabled: warehouseReady && view === "drivers" && !worklist,
      refetchInterval,
    },
  );

  const list = view === "routes" ? routes : drivers;
  const routesPage = jsonBody(routes.data);
  const driverRows = jsonBody(drivers.data)?.rows ?? [];
  const routeRows = routesPage?.rows ?? [];
  const openRoute = parseOpenRoute(search.open);

  const queryClient = useQueryClient();

  /**
   * Fetch the dashboard's data again now, rather than at the next tick of the
   * auto-refresh timer. Everything the dashboard reads from the service is
   * covered -- the summary, the list or worklist on screen -- except the
   * warehouse list, which is not a function of the filters.
   */
  const refresh = () =>
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        const path = queryKey[1];
        return (
          typeof path === "string" &&
          path.startsWith("/app/deliver/api/") &&
          !path.endsWith("/tenants")
        );
      },
    });

  const updateSearch = (patch: DeliverSearch) =>
    navigate({ search: patch, resetScroll: false });

  /**
   * Apply the filter bar, and make sure Search always does something. A query
   * is keyed by what it asks, so a Search that changes nothing the service is
   * asked -- the same filters again, which is exactly what someone chasing the
   * latest numbers presses it for -- would otherwise resolve to the cached
   * answer and appear dead. When the question is unchanged, ask it again.
   */
  const applyFilters = (patch: DeliverSearch) => {
    const unchanged =
      sameFilters(resolveFilters(patch, now, defaultTenantId), filters) &&
      (patch.view ?? "routes") === view &&
      patch.worklist === worklist &&
      page === 0;
    updateSearch(patch);
    if (unchanged) void refresh();
  };

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const allExpanded =
    routeRows.length > 0 && routeRows.every((r) => expanded.has(routeKey(r)));

  const exportWorkbook = async () => {
    setExportFailed(false);
    const params = {
      query: { ...query, page, size: PAGE_SIZE, format: "xlsx" as const },
    };
    const options = { params, parseAs: "blob" } as const;
    // The export is whatever is on screen: the open worklist, else the view.
    const { data, error } =
      worklist === "scan-exceptions"
        ? await fetchClient.GET("/app/deliver/api/exceptions", options)
        : worklist === "non-scanned"
          ? await fetchClient.GET("/app/deliver/api/non-scanned", options)
          : worklist === "invoice-exceptions"
            ? await fetchClient.GET(
                "/app/deliver/api/invoice-exceptions",
                options,
              )
            : worklist === "returns"
              ? await fetchClient.GET("/app/deliver/api/returns", options)
              : view === "routes"
                ? await fetchClient.GET("/app/deliver/api/routes", options)
                : await fetchClient.GET("/app/deliver/api/drivers", options);
    if (error !== undefined || !isBlob(data)) {
      setExportFailed(true);
      return;
    }
    downloadBlob(
      data,
      `deliver-${worklist ?? view}-${format(now, "yyyyMMdd")}.xlsx`,
    );
  };

  // The list's own fetch time, or the worklist's while one stands in for it.
  const updatedMs = worklist ? worklistUpdatedAt : list.dataUpdatedAt;
  const updatedAt = updatedMs ? format(new Date(updatedMs), "p") : undefined;

  return (
    <>
      <AppHeader />
      <div className="bg-surface h-full w-full">
        <div className="page-shell">
          <header className="page-header">
            <div className="page-header-left">
              <h1 className="page-title">{t("heading")}</h1>
              <p className="page-subtitle">{t("subheading")}</p>
            </div>
            <div className="page-header-right">
              <div className="page-header-actions">
                <Button
                  kind="text-icon-left"
                  variant="secondary"
                  icon="Settings"
                  text={t("manage.button")}
                  isDisabled={!warehouseReady}
                  onPress={() =>
                    navigate({
                      to: "/$lang/orgs/$org/deliver/manage/$tenant",
                      params: {
                        lang,
                        org,
                        tenant: String(filters.tenantId),
                      },
                    })
                  }
                  data-test-role="manage-warehouse"
                />
              </div>
            </div>
          </header>

          <FilterBar
            key={JSON.stringify(filters)}
            filters={filters}
            tenants={tenants.data?.rows ?? []}
            t={t}
            onApply={(next) =>
              applyFilters({ ...next, view: search.view, worklist })
            }
            onReset={() => applyFilters({ view: search.view, worklist })}
          />

          {!warehouseReady ? (
            tenants.isLoading ? (
              <Loader type="page" />
            ) : tenants.isError ? (
              <AppError
                description={t("warehouses.failed")}
                resolution={t("list.failureSupport")}
              />
            ) : (
              <EmptyState heading={t("warehouses.empty")} />
            )
          ) : (
            <>
              {summary.isLoading ? (
                <Loader type="page" />
              ) : summary.isError || !summary.data ? (
                <AppError
                  description={t("list.failed")}
                  resolution={t("list.failureSupport")}
                />
              ) : (
                <SummaryPanel
                  summary={summary.data}
                  worklist={worklist}
                  onWorklist={(next) =>
                    navigate({
                      search: (prev) => ({
                        ...prev,
                        worklist: next,
                        page: undefined,
                      }),
                      resetScroll: false,
                    })
                  }
                  lang={lang}
                  t={t}
                />
              )}

              <div className={styles.toolbar}>
                <div className={styles.listTitle}>
                  <h2 className={styles.listHeading}>
                    {worklist
                      ? t(`worklists.${WORKLIST_KEY[worklist]}.title`)
                      : t("list.heading")}
                  </h2>
                  {worklist ? (
                    <DismissibleChip
                      label={t(`worklists.${WORKLIST_KEY[worklist]}.title`)}
                      color={{ mode: "static", tone: "info" }}
                      dismissLabel={t("worklists.clear", {
                        name: t(`worklists.${WORKLIST_KEY[worklist]}.title`),
                      })}
                      onDismiss={() =>
                        navigate({
                          search: (prev) => ({
                            ...prev,
                            worklist: undefined,
                            page: undefined,
                          }),
                          resetScroll: false,
                        })
                      }
                    />
                  ) : null}
                </div>
                <div className={styles.toolbarActions}>
                  <Checkbox
                    name="autoRefresh"
                    value="true"
                    label={t("list.autoRefresh")}
                    isSelected={autoRefresh}
                    onChange={setAutoRefresh}
                  />
                  <Select
                    state={intervalSelectState({ interval, t })}
                    onMsg={onIntervalMsg}
                    display={{ trigger: { minWidth: "90px" } }}
                  />
                  {view === "routes" ? (
                    <Button
                      kind="text-only"
                      variant="tertiary"
                      text={
                        worklist
                          ? t("worklists.expandRoutesOnly")
                          : t(
                              allExpanded
                                ? "list.collapseAll"
                                : "list.expandAll",
                            )
                      }
                      isDisabled={
                        worklist !== undefined || routeRows.length === 0
                      }
                      onPress={() =>
                        setExpanded(
                          allExpanded
                            ? new Set()
                            : new Set(routeRows.map(routeKey)),
                        )
                      }
                    />
                  ) : null}
                  <Button
                    kind="text-only"
                    variant={view === "routes" ? "primary" : "tertiary"}
                    text={t("list.view.routes")}
                    onPress={() =>
                      navigate({
                        search: (prev) => ({
                          ...prev,
                          view: undefined,
                          worklist: undefined,
                          page: undefined,
                        }),
                        resetScroll: false,
                      })
                    }
                  />
                  <Button
                    kind="text-only"
                    variant={view === "drivers" ? "primary" : "tertiary"}
                    text={t("list.view.drivers")}
                    onPress={() =>
                      navigate({
                        search: (prev) => ({
                          ...prev,
                          view: "drivers",
                          worklist: undefined,
                          page: undefined,
                        }),
                        resetScroll: false,
                      })
                    }
                  />
                  <Button
                    kind="text-only"
                    variant="secondary"
                    text={t("list.export")}
                    onPress={exportWorkbook}
                  />
                  {updatedAt ? (
                    <span className={styles.updated}>
                      {t("list.updated", { time: updatedAt })}
                    </span>
                  ) : null}
                </div>
              </div>

              {exportFailed ? (
                <p role="alert" className={styles.exportFailed}>
                  {t("list.exportFailed")}
                </p>
              ) : null}

              <div className="table-frame">
                {worklist ? (
                  <WorklistTable
                    worklist={worklist}
                    filters={filters}
                    page={page}
                    onPage={(next) =>
                      navigate({
                        search: (prev) => ({
                          ...prev,
                          page: next > 0 ? next : undefined,
                        }),
                        resetScroll: false,
                      })
                    }
                    onOpenRoute={(route) =>
                      navigate({
                        search: (prev) => ({
                          ...prev,
                          open: formatOpenRoute(route),
                          tab: undefined,
                          stop: undefined,
                        }),
                        resetScroll: false,
                      })
                    }
                    refetchInterval={refetchInterval}
                    onUpdated={setWorklistUpdatedAt}
                    lang={lang}
                    t={t}
                  />
                ) : list.isLoading ? (
                  <Loader type="page" />
                ) : list.isError ? (
                  <AppError
                    description={t("list.failed")}
                    resolution={t("list.failureSupport")}
                  />
                ) : view === "routes" ? (
                  <RoutesTable
                    rows={routeRows}
                    expanded={expanded}
                    onToggle={toggle}
                    onOpen={(row) =>
                      navigate({
                        search: (prev) => ({
                          ...prev,
                          open: formatOpenRoute(row),
                          tab: undefined,
                          stop: undefined,
                        }),
                        resetScroll: false,
                      })
                    }
                    lang={lang}
                    t={t}
                  />
                ) : (
                  <DriversTable rows={driverRows} lang={lang} t={t} />
                )}
              </div>

              {view === "routes" &&
              !worklist &&
              (page > 0 || routesPage?.hasMore) ? (
                <div className={styles.pager}>
                  <Button
                    kind="text-only"
                    variant="tertiary"
                    text={t("list.previous")}
                    isDisabled={page === 0 || routes.isFetching}
                    onPress={() =>
                      navigate({
                        search: (prev) => ({
                          ...prev,
                          page: page - 1 > 0 ? page - 1 : undefined,
                        }),
                        resetScroll: false,
                      })
                    }
                  />
                  <span>{t("list.pageOf", { page: page + 1 })}</span>
                  <Button
                    kind="text-only"
                    variant="tertiary"
                    text={t("list.next")}
                    isDisabled={!routesPage?.hasMore || routes.isFetching}
                    onPress={() =>
                      navigate({
                        search: (prev) => ({ ...prev, page: page + 1 }),
                        resetScroll: false,
                      })
                    }
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {openRoute ? (
        <RouteDrawer
          key={search.open}
          route={openRoute}
          tab={search.tab}
          stop={search.stop}
          lang={lang}
          t={t}
          onTabChange={(tab) =>
            navigate({
              search: (prev) => ({
                ...prev,
                tab: tab === "summary" ? undefined : tab,
              }),
              resetScroll: false,
            })
          }
          onStopChange={(stop) =>
            navigate({
              search: (prev) => ({ ...prev, stop }),
              resetScroll: false,
            })
          }
          onViewStop={(stop) =>
            navigate({
              search: (prev) => ({ ...prev, tab: "items", stop }),
              resetScroll: false,
            })
          }
          onClose={() =>
            navigate({
              search: (prev) => ({
                ...prev,
                open: undefined,
                tab: undefined,
                stop: undefined,
              }),
              resetScroll: false,
            })
          }
        />
      ) : null}
    </>
  );
}
